import { type FC } from "react";
import { Check } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { Button } from "@/shared/components/ui/button";

interface ChecklistStep {
  label: string;
  description: string;
  status: "completed" | "active" | "upcoming";
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface AccountSetupChecklistProps {
  steps: ChecklistStep[];
}

export const AccountSetupChecklist: FC<AccountSetupChecklistProps> = ({ steps }) => {
  return (
    <div className="flex w-full sm:w-[500px] flex-col overflow-clip rounded-lg border border-primary">
      {steps.map((step, index) => {
        const isCompleted = step.status === "completed";
        const isUpcoming = step.status === "upcoming";

        return (
          <div
            key={step.label}
            className={cn(
              "flex items-center gap-4 border-b border-primary bg-white p-4 last:border-b-0",
            )}
          >
            {isCompleted ? (
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                <Check
                  className="h-3.5 w-3.5 text-white"
                  strokeWidth={3}
                  role="img"
                  aria-label="Completed"
                />
              </div>
            ) : (
              <div
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-white",
                  isUpcoming && "opacity-50",
                )}
              >
                <span className="text-sm font-medium leading-none text-center">{index + 1}</span>
              </div>
            )}

            <div className="flex flex-1 flex-col">
              <p className={cn("text-sm font-semibold", isUpcoming && "text-tertiary")}>
                {step.label}
              </p>
              <p className={cn("text-sm", isUpcoming ? "text-tertiary" : "text-secondary")}>
                {step.description}
              </p>
            </div>

            {step.action ? (
              <Button variant="brand" className="shrink-0" onClick={step.action.onClick}>
                {step.action.label}
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
